import React from 'react';
import cx from 'classnames';
import {faTrash, faArrowUp, faArrowDown} from '@fortawesome/free-solid-svg-icons';
import Button from '../../../../../components/Button';
import IconButton from '../../../../../components/IconButton';
import FieldConfiguration from '../FieldConfiguration';
import OptionManagement from '../OptionManagement';
import {FormField} from '../../../../../types/ContactForm';
import styles from './FieldList.module.scss';

interface FieldWithIndex {
    field: FormField;
    originalIndex: number;
}

interface FieldListProps {
    fieldsInCurrentStep: FieldWithIndex[];
    allFields: FormField[];
    onUpdateField: (index: number, property: string, value: any) => void;
    onRemoveField: (index: number) => void;
    onMoveFieldInStep: (fieldsInStep: FieldWithIndex[], fieldIndex: number, direction: 'up' | 'down') => void;
    onAddFieldToCurrentStep: () => void;
    onUpdateOption: (fieldIndex: number, optionIndex: number, value: string) => void;
    onAddOption: (fieldIndex: number) => void;
    onRemoveOption: (fieldIndex: number, optionIndex: number) => void;
    onCleanupEmptyOptions: (fieldIndex: number) => void;
    saveAllSettings: () => Promise<void>;
}

const FieldList: React.FC<FieldListProps> = ({
                                                 fieldsInCurrentStep,
                                                 allFields,
                                                 onUpdateField,
                                                 onRemoveField,
                                                 onMoveFieldInStep,
                                                 onAddFieldToCurrentStep,
                                                 onUpdateOption,
                                                 onAddOption,
                                                 onRemoveOption,
                                                 onCleanupEmptyOptions,
                                                 saveAllSettings
                                             }) => {

    if (fieldsInCurrentStep.length === 0) {
        return (
            <div className={cx(styles['no-fields'], 'zr-mb-4')}>
                <p>Noch keine Felder in diesem Schritt hinzugefügt.</p>
                <Button
                    hierarchy="primary"
                    text="+ Erstes Feld hinzufügen"
                    fn={onAddFieldToCurrentStep}
                />
            </div>
        );
    }

    return (
        <div className={cx(styles['fields-container'])}>
            {fieldsInCurrentStep.map(({field, originalIndex}, stepFieldIndex) => {
                return (
                    <div key={field.id} className={cx(styles['field-container'])}>
                        <div className={cx(styles['field-content'])}>
                            <FieldConfiguration
                                field={field}
                                fieldIndex={originalIndex}
                                onUpdateField={onUpdateField}
                                allFields={allFields}
                            />

                            {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                                <OptionManagement
                                    fieldType={field.type as 'select' | 'radio' | 'checkbox'}
                                    options={field.options || ''}
                                    fieldIndex={originalIndex}
                                    onUpdateOption={onUpdateOption}
                                    onAddOption={onAddOption}
                                    onRemoveOption={onRemoveOption}
                                    onCleanupEmptyOptions={onCleanupEmptyOptions}
                                />
                            )}
                        </div>

                        <div className={cx(styles['field-actions'])}>
                            <IconButton
                                icon={faArrowUp}
                                color="#64748b"
                                onClick={() => onMoveFieldInStep(fieldsInCurrentStep, stepFieldIndex, 'up')}
                                title="Feld nach oben"
                                classes={cx(styles['action-btn'], styles['move-up-btn'], {
                                    [styles['disabled']]: stepFieldIndex === 0
                                })}
                                style={{
                                    opacity: stepFieldIndex === 0 ? 0.3 : 1,
                                    pointerEvents: stepFieldIndex === 0 ? 'none' : 'auto'
                                }}
                            />

                            <IconButton
                                icon={faArrowDown}
                                color="#64748b"
                                onClick={() => onMoveFieldInStep(fieldsInCurrentStep, stepFieldIndex, 'down')}
                                title="Feld nach unten"
                                classes={cx(styles['action-btn'], styles['move-down-btn'], {
                                    [styles['disabled']]: stepFieldIndex === fieldsInCurrentStep.length - 1
                                })}
                                style={{
                                    opacity: stepFieldIndex === fieldsInCurrentStep.length - 1 ? 0.3 : 1,
                                    pointerEvents: stepFieldIndex === fieldsInCurrentStep.length - 1 ? 'none' : 'auto'
                                }}
                            />

                            <div className={cx(styles['action-divider'])}></div>

                            <IconButton
                                icon={faTrash}
                                color="#64748b"
                                onClick={() => onRemoveField(originalIndex)}
                                title="Feld entfernen"
                                classes={cx(styles['action-btn'])}
                            />
                        </div>
                    </div>
                );
            })}


            <div className={cx(styles['step-actions'])}>
                <div>
                    <Button
                        hierarchy="primary"
                        text="Feld hinzufügen +"
                        fn={onAddFieldToCurrentStep}
                    />
                </div>
                <div>
                    <Button
                        hierarchy="secondary"
                        text="Alle Einstellungen speichern"
                        fn={saveAllSettings}
                    />
                </div>
            </div>
        </div>
    );
};

export default FieldList; 